import { createContext, useContext, useEffect, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { IMainState } from 'store/states'
import * as userActions from 'store/actions/user.action'
import { IPermissions } from 'models/permission.model'
import { IBasicProfile } from 'models/lmsProfile.model'

export interface AccessContextProps {
  modules: {
    administration: boolean
    learningPaths: boolean
    monitoring: boolean
    reports: boolean
  }
  loading: boolean
  permissions: {
    all: boolean
  } & IPermissions
  roles: {
    admin: boolean
    superadmin: boolean
    moderator: boolean
    supervisor: boolean
  }
  lmsProfiles: (IPermissions & {
    profile: IBasicProfile
  })[]
}

export const AccessContext = createContext<AccessContextProps>(
  Object.create(null)
)

export const AccessProvider = ({ children }: { children: React.ReactNode }) => {
  const permission = useSelector(({ user }: IMainState) => user.permission)

  const { admin, superadmin, moderator, supervisor, permissions } = permission

  const all = admin || superadmin
  const administration = useMemo(() => {
    const { moderator } = permission
    const auxModerator =
      moderator && (permissions.canEdit || permissions.canManageCourses)
    return all || auxModerator
  }, [permission, permissions, all])

  const monitoring = useMemo(() => {
    const { permissions, supervisor } = permission
    const { canMonitoringAreas, canMonitor } = { ...permissions }
    return canMonitoringAreas || canMonitor || all || supervisor
  }, [permission, all])

  const lmsProfiles = useMemo(() => permission.lmsProfiles || [], [permission])

  const dispatch = useDispatch()

  useEffect(() => {
    dispatch({ type: userActions.GET_PERMISSIONS })
  }, [dispatch])

  return (
    <AccessContext.Provider
      value={{
        modules: {
          administration,
          learningPaths: all,
          monitoring,
          reports: all,
        },
        loading: permission.loading,
        permissions: {
          all,
          canAnswer: !!permissions?.canAnswer || all,
          canDownload: !!permissions?.canDownload || all,
          canEdit: !!permissions?.canEdit || all,
          canMonitor: !!permissions?.canMonitor || all,
          canRequest: !!permissions?.canRequest || all,
          canMonitoringAreas: !!permissions?.canMonitoringAreas || all,
          canManageCourses: !!permissions?.canManageCourses || all,
        },
        roles: {
          admin,
          superadmin,
          moderator,
          supervisor,
        },
        lmsProfiles,
      }}
    >
      {children}
    </AccessContext.Provider>
  )
}
export const AccessConsumer = AccessContext.Consumer

export const useAccess = () => useContext(AccessContext)
